import { Component, createRef } from 'preact';
import { cn } from '../lib/utils';
import { isFoilFinish } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../utils/sorcery/foilParams.js';
import CardImagePlaceholder from './CardImagePlaceholder';
import FoilCanvas from './FoilCanvas';

const TILT_MAX_DEG = 10;
// Hover dwell before mounting the live foil canvas. Fly-over (the mouse passing
// across a grid) never settles, so it never spins up a WebGL context. Only a
// deliberate hover does. Keeps live foil contexts to ~1 (the hovered card).
const HOVER_LIVE_DELAY_MS = 70;
const HOVER_SCALE = 1.04;
const SITE_SCALE = 1.08;

function getFrontImageUrl(entry) {
  return entry.printing?.image_url || entry.card?.printings?.[0]?.image_url || '';
}

// Back-face printings carry the same slug as the front + a suffix, so
// match by stripping the suffix and comparing. Falls back to the
// first back printing if the entry's front printing isn't in the
// back-face whitelist (e.g. Promotional Druid reprints. Those are
// not flippable).
function getBackImageUrl(entry) {
  const back = entry.card?.backFace;
  if (!back?.printings?.length) return '';
  const frontSlug = entry.printing?.unique_id || entry.card?.printings?.[0]?.unique_id;
  const match = back.printings.find((p) => p.unique_id.startsWith(frontSlug + '-'));
  return (match || back.printings[0]).image_url;
}

export function getImageUrl(entry, face) {
  return face === 'back' ? getBackImageUrl(entry) : getFrontImageUrl(entry);
}

function getFoiling(entry) {
  return entry.printing?.foiling || 'S';
}

export default class DeckCardTile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      rotateX: 0, rotateY: 0, hovering: false,
      mx: 50, my: 50,
      originX: 'center', originY: 'center',
      nudgeX: 0, nudgeY: 0,
      // Image load tracking. Starts false so the medieval placeholder
      // shows while the fetch is in flight, then flips true on the
      // <img>'s onLoad to fade the real image in over the placeholder.
      // `imgFailed` flips true on a 404/onError so the placeholder stops
      // spinning forever when the URL can't be resolved. The placeholder
      // stays visible, but the spinner is hidden so the tile doesn't
      // pretend to still be loading.
      imgLoaded: false,
      imgFailed: false,
      // True while the per-tile liveFoil FoilCanvas is actually drawing over
      // this card (pack-opening 3D fan, Phase 2). We then hide the DOM <img> so
      // a plain (mask-less) copy can't bleed through. The shared-stage path no
      // longer uses this (the opaque stage quad covers the <img>).
      foilCovering: false,
      // True once the live FoilCanvas is mounted for a deliberate hover (armed
      // after HOVER_LIVE_DELAY_MS so fly-over doesn't churn WebGL contexts).
      liveHover: false,
    };
    this.ref = createRef();
    this.faceRef = createRef();
    this.imgRef = createRef();
  }

  componentDidMount() {
    // Already decoded at mount (cache hit, or a re-mount as the virtualized
    // grid scrolls back over this row): a load event that fired before this
    // handler attached would otherwise strand the skeleton.
    const img = this.imgRef.current;
    if (img?.complete && img.naturalWidth > 0) this.setState({ imgLoaded: true });
  }

  componentDidUpdate(prevProps) {
    // If the underlying image URL changed (e.g. user switched printings,
    // parent swapped in a different card entry, or the user flipped
    // the card via the controlled `face` prop) reset so the
    // placeholder shows again until the new images load.
    const prevUrl = getImageUrl(prevProps.entry, prevProps.face);
    const nextUrl = getImageUrl(this.props.entry, this.props.face);
    if (prevUrl !== nextUrl && (this.state.imgLoaded || this.state.imgFailed)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ imgLoaded: false, imgFailed: false });
    }
  }

  componentWillUnmount() {
    if (this._moveRaf) cancelAnimationFrame(this._moveRaf);
    if (this._hoverTimer) clearTimeout(this._hoverTimer);
  }

  // True when a Site should do the 90° flip on hover. `disableSiteRotation`
  // opts out entirely (tiny thumbnail rows); `siteHorizontal` opts out because
  // the tile already renders the Site horizontally at rest, so flipping it on
  // hover would stand it back up.
  isSiteFlip() {
    const { entry, disableSiteRotation, siteHorizontal } = this.props;
    return !!entry?.card?.played_horizontally && !disableSiteRotation && !siteHorizontal;
  }

  handleImageError = () => {
    this.setState({ imgFailed: true });
  };

  handleImageLoad = () => {
    this.setState({ imgLoaded: true });
  };

  handleMouseMove = (e) => {
    // Store raw event coords and throttle to one update per frame
    this._pendingX = e.clientX;
    this._pendingY = e.clientY;

    if (this._moveRaf) return;
    this._moveRaf = requestAnimationFrame(() => {
      this._moveRaf = null;
      const el = this.ref.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (this._pendingX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (this._pendingY - rect.top) / rect.height));

      this.setState({
        rotateY: (x - 0.5) * 2 * TILT_MAX_DEG,
        rotateX: (0.5 - y) * 2 * TILT_MAX_DEG,
        mx: x * 100,
        my: y * 100,
      });

      // Feed the shared foil scene the live hover tilt + pivot (imageHidden
      // mode): the scene rotates/scales the quad (real perspective pivot) since
      // the DOM chrome stays flat. rotateX/Y match the legacy CSS card tilt.
      if (this.props.imageHidden) {
        const mx = x * 100;
        const my = y * 100;
        const fromCenter = Math.min(1, Math.hypot(mx / 100 - 0.5, my / 100 - 0.5) / 0.5);
        const isSite = this.isSiteFlip();
        this.props.onTilt?.({
          mx, my, fromCenter, hovering: true,
          rotateX: (0.5 - y) * 2 * TILT_MAX_DEG,
          rotateY: (x - 0.5) * 2 * TILT_MAX_DEG,
          scale: isSite ? SITE_SCALE : HOVER_SCALE,
          rotateZ: isSite ? 90 : 0,
          nudgeX: this._foilNudgeX || 0,
          nudgeY: this._foilNudgeY || 0,
        });
      }
    });
    // NOTE: do NOT re-arm the settle timer on move.
  };

  handleMouseEnter = () => {
    // Arm the live-foil mount on a short dwell. Only for surfaces that still
    // use a per-tile FoilCanvas (NOT imageHidden scene cards, which would churn
    // the WebGL context limit; their hover pivot is drawn by the shared scene).
    if (!this.props.imageHidden && isFoilFinish(getFoiling(this.props.entry))) {
      if (this._hoverTimer) clearTimeout(this._hoverTimer);
      this._hoverTimer = setTimeout(() => {
        this._hoverTimer = null;
        this.setState({ liveHover: true });
      }, HOVER_LIVE_DELAY_MS);
    }

    const el = this.ref.current;
    if (!el) {
      this.setState({ hovering: true });
      if (this.props.imageHidden) {
        const siteEnter = this.isSiteFlip();
        this.props.onTilt?.({ mx: 50, my: 50, fromCenter: 0, hovering: true, rotateX: 0, rotateY: 0, scale: siteEnter ? SITE_SCALE : HOVER_SCALE, rotateZ: siteEnter ? 90 : 0, nudgeX: 0, nudgeY: 0 });
      }
      this.props.onHoverChange?.(true);
      return;
    }

    const rect = el.getBoundingClientRect();
    // `disableSiteRotation` lets callers (e.g. the basic-sites picker
    // in DraftDeckBuilder) opt out of the 90° flip + larger scale that
    // sites get on hover. Tiny thumbnail rows look broken with the
    // flip, but everything else (tilt, lift, foil) stays.
    const isSite = this.isSiteFlip();

    // Find the nearest clipping ancestor to measure available space
    let clipLeft = 0;
    let clipRight = window.innerWidth;
    let clipTop = 0;
    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      const style = getComputedStyle(parent);
      const ov = style.overflow + style.overflowX + style.overflowY;
      if (ov.includes('auto') || ov.includes('scroll') || ov.includes('hidden')) {
        const pr = parent.getBoundingClientRect();
        clipLeft = Math.max(clipLeft, pr.left);
        clipRight = Math.min(clipRight, pr.right);
        clipTop = Math.max(clipTop, pr.top);
        break;
      }
      parent = parent.parentElement;
    }

    // Compute where the transformed card edges would land
    const w = rect.width;
    const h = rect.height;
    const cx = rect.left + w / 2;
    const cy = rect.top + h / 2;

    let originX = 'center';
    let originY = 'center';
    let nudgeX = 0;
    let nudgeY = 0;

    if (isSite) {
      // Site rotated 90° from center: visual width = h * scale, visual height = w * scale
      // Always use center origin for sites. Nudge via translate instead
      const visW = h * SITE_SCALE;
      const visH = w * SITE_SCALE;
      const leftEdge = cx - visW / 2;
      const rightEdge = cx + visW / 2;
      const topEdge = cy - visH / 2;

      if (leftEdge < clipLeft + 6) nudgeX = (clipLeft + 6) - leftEdge;
      else if (rightEdge > clipRight - 6) nudgeX = (clipRight - 6) - rightEdge;
      if (topEdge < clipTop + 6) nudgeY = (clipTop + 6) - topEdge;
    } else {
      // Regular card: shift origin toward nearest edge
      const expandX = w * (HOVER_SCALE - 1) / 2;
      const expandY = h * (HOVER_SCALE - 1) / 2;

      if (rect.left - expandX < clipLeft + 4) originX = 'left';
      else if (rect.right + expandX > clipRight - 4) originX = 'right';
      if (rect.top - expandY < clipTop + 4) originY = 'top';
    }

    this.setState({ hovering: true, originX, originY, nudgeX, nudgeY });
    // Store the nudge synchronously: handleMouseMove must NOT read it from state
    // (setState is async, so the first move would read a stale 0 and fling the
    // card off-screen before correcting).
    this._foilNudgeX = nudgeX;
    this._foilNudgeY = nudgeY;
    // Feed the scene the hover pivot + the site keep-in-view nudge (same nudgeX/
    // nudgeY the DOM path applies via translate) so foil sites stay fully visible
    // exactly like the legacy grid.
    if (this.props.imageHidden) {
      this.props.onTilt?.({
        mx: 50, my: 50, fromCenter: 0, hovering: true,
        rotateX: 0, rotateY: 0,
        scale: isSite ? SITE_SCALE : HOVER_SCALE,
        rotateZ: isSite ? 90 : 0,
        nudgeX, nudgeY,
      });
    }
    this.props.onHoverChange?.(true);
  };

  handleMouseLeave = () => {
    if (this._hoverTimer) { clearTimeout(this._hoverTimer); this._hoverTimer = null; }
    // Cancel any pending move-rAF: if it fired AFTER this leave it would re-emit
    // a hovered tilt and leave the scene quad stuck mid-pivot.
    if (this._moveRaf) { cancelAnimationFrame(this._moveRaf); this._moveRaf = null; }
    this._foilNudgeX = 0;
    this._foilNudgeY = 0;
    this.setState({
      rotateX: 0, rotateY: 0, hovering: false, liveHover: false,
      mx: 50, my: 50, nudgeX: 0, nudgeY: 0,
    });
    if (this.props.imageHidden) this.props.onTilt?.({ mx: 50, my: 50, fromCenter: 0, hovering: false, rotateX: 0, rotateY: 0, scale: 1, rotateZ: 0, nudgeX: 0, nudgeY: 0 });
    this.props.onHoverChange?.(false);
  };

  render() {
    const { entry, isSelected, onClick, onContextMenu, cornerBadge, face = 'front', siteHorizontal = false, imageHidden = false } = this.props;
    const { rotateX, rotateY, hovering, mx, my, originX, originY, nudgeX, nudgeY } = this.state;
    const isSite = this.isSiteFlip();
    // `siteHorizontal`: render the Site landscape at rest instead of standing it
    // up and flipping it on hover. The tile box turns landscape and the card
    // face (still portrait, so the rounded-corner mask fits) is rotated 90°
    // inside it. The hover tilt above stays in screen space.
    const horizontal = !!entry.card?.played_horizontally && siteHorizontal;
    const imgUrl = getImageUrl(entry, face);
    const foiling = getFoiling(entry);
    const isFoil = isFoilFinish(foiling);

    const siteHovered = isSite && hovering;

    const dx = (mx / 100) - 0.5;
    const dy = (my / 100) - 0.5;
    const fromCenter = Math.min(1, Math.sqrt(dx * dx + dy * dy) / 0.5);
    const bgX = 20 + (mx / 100) * 60;
    const bgY = 20 + (my / 100) * 60;
    const sheenAngle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    const sheenOffset = (0.5 - (mx / 100)) * 100;

    const foilStyle = isFoil ? {
      '--foil-bg-x': `${bgX}%`,
      '--foil-bg-y': `${bgY}%`,
      // Tilt-driven intensity (glare opacity + brightness), dialed back
      // 10% so extreme pivots aren't too harsh. Center is fromCenter≈0,
      // so the neutral front-facing look is unchanged.
      '--foil-d': fromCenter * 0.9,
      '--foil-angle': `${sheenAngle}deg`,
      '--foil-offset': `${sheenOffset}%`,
      // Tilt-driven emboss offset for the white zones (name plate + text
      // box): shadow shifts away from the cursor/light, highlight toward
      // it, so those areas read as raised above the card. 0 at rest.
      '--emboss-dx': `${dx * 5}px`,
      '--emboss-dy': `${dy * 5}px`,
    } : {};

    const nudge = (nudgeX || nudgeY) ? `translate(${nudgeX}px, ${nudgeY}px) ` : '';
    // The tile transforms identically whether the image is a DOM <img> (non-foil)
    // or drawn by the shared foil canvas (imageHidden). For imageHidden the tile
    // is transparent (the canvas draws the visual, following the SAME transform
    // via onTilt), but it MUST still transform so the (invisible) hit-area +
    // keep-in-view nudge track the rotated/lifted card. Otherwise hover is
    // detected at the card's original rect, which oscillates when it rotates 90°.
    let innerTransform;
    if (siteHovered) {
      innerTransform = `${nudge}rotateX(${rotateX}deg) rotateY(${rotateY}deg) rotate(90deg) scale(${SITE_SCALE})`;
    } else if (hovering) {
      innerTransform = `${nudge}rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${HOVER_SCALE})`;
    } else {
      innerTransform = 'none';
    }

    return (
      <div
        ref={this.ref}
        role="option"
        aria-label={entry.card.name}
        aria-selected={String(isSelected)}
        tabIndex={isSelected ? 0 : -1}
        data-deck-entry-index={entry.entryIndex}
        className={cn(
          'deck-card-tile cursor-pointer',
          horizontal ? 'rounded-card-landscape' : 'rounded-card',
          isSelected && 'ring-2 ring-primary/60 shadow-[0_0_20px_rgba(180,140,60,0.25)]',
          hovering && 'deck-card-tile--hover',
          siteHovered && 'deck-card-tile--site-hover',
        )}
        style={{
          perspective: '600px',
          ...(horizontal ? { aspectRatio: '88 / 63' } : null),
          // imageHidden: the card is drawn by the shared scene canvas BELOW this
          // DOM tile, so the tile's muted placeholder bg would tint it. Make the
          // tile fully transparent so the canvas card shows at full opacity.
          ...(imageHidden ? { background: 'transparent' } : null),
        }}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onMouseMove={this.handleMouseMove}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        {/* Glow wrapper. No overflow restrictions, carries transform + shadow */}
        <div
          className={cn('h-full w-full', horizontal ? 'rounded-card-landscape' : 'rounded-card')}
          style={{
            transform: innerTransform,
            transformOrigin: `${originX} ${originY}`,
            transition: hovering
              ? 'transform 0.15s ease-out'
              : 'transform 0.35s ease-out',
            transformStyle: 'preserve-3d',
            // imageHidden: no DOM glow/shadow. The scene draws the card; a
            // box-shadow here would just be an amber outline around an empty box.
            boxShadow: imageHidden ? undefined : ([
              isFoil && foiling === 'R' && '0 0 14px rgba(160,120,255,0.4), 0 0 30px rgba(100,180,255,0.15)',
              isFoil && foiling !== 'R' && '0 0 10px rgba(200,170,80,0.35), 0 0 24px rgba(200,170,80,0.12)',
              siteHovered && '0 14px 40px rgba(0,0,0,0.55)',
              !siteHovered && hovering && '0 8px 24px rgba(0,0,0,0.45)',
            ].filter(Boolean).join(', ') || undefined),
          }}
        >
          {/* Inner card. Overflow:hidden masks the image + foil layers
              (base art, baked rest <img>, live FoilCanvas) to the rounded
              card shape. */}
          <div
            ref={this.faceRef}
            className={cn(
              'deck-card-tile-inner overflow-hidden rounded-card',
              horizontal ? 'absolute left-1/2 top-1/2' : 'relative h-full w-full',
            )}
            style={{
              ...foilStyle,
              ...(horizontal ? {
                width: 'calc(100% * 63 / 88)',
                aspectRatio: '63 / 88',
                transform: 'translate(-50%, -50%) rotate(90deg)',
              } : null),
            }}
          >
            {imageHidden ? null : (
            <>
            {/* No `loading="lazy"`: the grid is virtualized, so a mounted tile
                is already within a few rows of the viewport. Deferring the
                fetch to the intersection observer on top of that meant art
                only started downloading once the tile was basically on
                screen. The "cards pop in at random while scrolling" effect. */}
            <img
              ref={this.imgRef}
              src={imgUrl}
              alt={entry.card.name}
              decoding="async"
              onLoad={this.handleImageLoad}
              onError={this.handleImageError}
              className="deck-card-tile-image relative h-full w-full object-cover"
              style={{ opacity: this.state.foilCovering ? 0 : 1 }}
            />
            {/* Skeleton sits over the art and is removed the moment it
                loads. No reveal animation. */}
            {!this.state.imgLoaded ? (
              <div className="card-skeleton absolute inset-0" style={{ zIndex: 2 }}>
                <CardImagePlaceholder variant="skeleton" spinnerSize={0} />
              </div>
            ) : null}
            {/* Live WebGL foil (full shader + tilt). Mounted ONLY for the one
                hovered/focused card (liveHover, armed on dwell) or an explicit
                liveFoil surface (pack-opening fan). Rides the CSS transform, so
                3D tilt + the 90° site flip are tracked for free. onCover hides
                the rest <img> the instant the live frame paints -> seamless
                handoff (both share the uTilt=0 rest frame). */}
            {isFoil && (this.props.liveFoil || this.state.liveHover) && this.state.imgLoaded && !this.state.imgFailed ? (
              <FoilCanvas
                imageUrl={imgUrl}
                tilt={this.props.foilTilt || { mx, my, fromCenter }}
                scrollSweep={false}
                zones={foilZonesForCard(entry.card)}
                params={DEFAULT_FOIL_PARAMS}
                onCover={(v) => this.setState({ foilCovering: v })}
              />
            ) : null}
            {hovering ? (
              <div
                className="card-sheen"
                style={{
                  '--sheen-angle': `${sheenAngle}deg`,
                  '--sheen-offset': `${sheenOffset}%`,
                  '--sheen-d': fromCenter,
                }}
              />
            ) : null}
            </>
            )}
          </div>
          {/* In-card overlay slot. Lives inside the glow wrapper so
              it rides every card transform (hover tilt, foil pivot,
              site flip) but sits outside deck-card-tile-inner so the
              card mask never clips it. No site-flip override anywhere
. When a Site card flips, the badge flips with it. */}
          {cornerBadge ?? null}
        </div>
      </div>
    );
  }
}
